import React from 'react';
import { Helmet } from 'react-helmet';
import { navigate, RouteComponentProps } from '@reach/router';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { isCompanyOwnerOrSupport } from 'store/companies/companiesUtils';
import { IUsers, RootState } from 'store/store';
import Button from 'components/Button';
import Icon from 'components/Icon';
import messages from './Messages';
import notify from 'components/Notify';
import LogoLoading from 'components/LogoLoading';
import { IOption } from 'components/Select/Select';
import { display } from 'components/ErrorView/ErrorDialog';
import { IChipItem } from 'components/SelectChips/SelectChips';
import CompanyInviteList from './CompanyInviteList';
import CompanyMembersListContainer from './CompanyMembersListContainer';
import { CompanyMembersWrapper } from './styles';
import { appDispatch } from 'store/store';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  removeMemberFromCompany,
  removeMemberFromCompanyFn,
} from 'store/companies/companiesSlice';
import {
  ICompany,
  ICompanyMember,
  ICompanyRole,
  IProjectStore,
  IUserRoleView,
  TProjects,
} from 'store/companies/companiesTypes';
import { addGuideToList } from 'store/siteGuide/SiteGuideSlice';
import { memoize } from 'lodash';
import i18n from '../../../i18n';

interface IStateToProps {
  users: IUsers['map'];
  company: ICompany;
  projects: TProjects;
  companyOwnerId: number;
  isCompanyOwner: boolean;
  isCompanyAdmin: boolean;
  thisUserId: number;
}

interface IDispatchToProps {
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  removeMemberFromCompany: (
    companyMember: ICompanyMember
  ) => ReturnType<typeof removeMemberFromCompanyFn>;
  addGuideToList: (key: Array<string>) => any;
}

interface IOwnProps {
  companyId?: number;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  loading: boolean;
  inviteView: boolean;
  filterUserWithName?: string;
}

class CompanyMembers extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    loading: true,
    inviteView: false,
  };

  componentDidMount() {
    if (!this.props.isCompanyAdmin) {
      navigate('/notfound');
    }
    const membersList = Object.keys(this.props.company.members);
    this.props.requireSeveralUser(membersList.map((it) => Number(it))).then(
      () => {
        this.setState({
          loading: false,
        });
      },
      (err: any) => {
        display(err);
      }
    );
    this.props.addGuideToList(['مدیریت کاربران']);
  }

  handleDeleteMember = (userId: number, memberName: string) => {
    const companyMember = {
      userId,
      companyId: Number(this.props.companyId),
    } as ICompanyMember;
    this.props.removeMemberFromCompany(companyMember).then(
      () => {
        notify(messages.memberRemoved(memberName));
      },
      (err: any) => {
        display(err);
      }
    );
  };

  handleChangeView = (state: boolean) => () => {
    this.setState({ inviteView: state, filterUserWithName: undefined });
  };

  render() {
    if (this.state.loading) {
      return <LogoLoading />;
    }
    const { users, company, companyOwnerId, projects } = this.props;

    const companyRoleChips: string[] = [];
    const selectableCompanyRoleChips: string[] = [];
    const companyRoleOptions: IOption[] = [];
    Object.values(this.props.company.roles).forEach((role: ICompanyRole) => {
      companyRoleChips.push(role.name);
      if (!role.compoundCreationWithRoleId && role.roleType !== 'InProject') {
        selectableCompanyRoleChips.push(role.name);
      }
      if (role.roleType !== 'InProject')
        companyRoleOptions.push({
          value: role.id,
          label: role.displayName || '',
        });
    });

    const userRoleListMap = new Map<number, IChipItem[]>();
    Object.values(this.props.company.roles).forEach((role) => {
      if (role.users.length > 0) {
        role.users.forEach((roleItem: IUserRoleView) => {
          const currentList = userRoleListMap.get(roleItem.userId) || [];
          const disabled = roleItem.roleId !== role.id;
          currentList.push({
            id: role.id,
            title: role.displayName!,
            roleType: role.roleType,
            disabled: disabled,
            userId: roleItem.userId,
          });
          userRoleListMap.set(roleItem.userId, currentList);
        });
      }
    });
    const getMemberRoles = (userId: number) => {
      return userRoleListMap.get(userId) || [];
    };

    const memoizeGetMemberRoles = memoize(getMemberRoles);

    return (
      <React.Fragment>
        <Helmet>
          <title>
            {i18n.t('companyMembers', { ns: 'companyManagement' }) as string}
          </title>
          <meta
            name="description"
            content={
              i18n.t('companyMembers', { ns: 'companyManagement' }) as string
            }
          />
        </Helmet>
        <CompanyMembersWrapper>
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <Button
              color="green-outline"
              onClick={this.handleChangeView(!this.state.inviteView)}
            >
              {!this.state.inviteView ? (
                <>
                  <Icon type="user-plus" />
                  {
                    i18n.t('addMemberToCompany', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </>
              ) : (
                <>
                  <Icon type="users" />
                  {
                    i18n.t('membersList', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </>
              )}
            </Button>
          </div>
          <CompanyInviteList
            isActive={this.state.inviteView}
            companyId={company.id}
            companyName={company.name}
            companyRoleChips={companyRoleChips}
            selectableCompanyRoleChips={selectableCompanyRoleChips}
            filterUserWithName={(name: string) => {
              this.setState({ inviteView: false, filterUserWithName: name });
            }}
          />
          {!this.state.inviteView ? (
            Object.keys(company.members).length > 0 ? (
              <CompanyMembersListContainer
                users={users}
                onDelete={this.handleDeleteMember}
                companyId={company.id}
                getMemberRoles={memoizeGetMemberRoles}
                companyOwnerId={companyOwnerId}
                companyRoleOptions={companyRoleOptions}
                companyMembers={Object.values(company.members)}
                company={company}
                isSupport={isCompanyOwnerOrSupport(
                  this.props.thisUserId,
                  company
                )}
                companyRoles={company.roles}
                companyMembersMap={company.members}
                projects={projects}
                thisUserId={this.props.thisUserId}
                filterUserName={this.state.filterUserWithName}
              />
            ) : (
              <span>
                {
                  i18n.t('thereIsNoMemberYet', {
                    ns: 'companyManagement',
                  }) as string
                }
              </span>
            )
          ) : null}
        </CompanyMembersWrapper>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);
  const company = state.companies.data[companyId];
  const companyOwnerId: number = company.owner.userId;
  const thisUserId: number = Number(state.thisUser.userId);
  const isCompanyOwner = thisUserId === companyOwnerId;
  const isCompanyAdmin = company?.members[thisUserId].admin;
  const users = state.users.map;
  const projects: { [key: number]: IProjectStore } = {};
  Object.values(state.companies.data).forEach((comp) => {
    Object.assign(projects, comp.projects);
  });
  return {
    company,
    users,
    companyOwnerId,
    isCompanyOwner,
    projects,
    isCompanyAdmin,
    thisUserId,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  requireSeveralUser: (usersList: number[]) =>
    appDispatch(requireSeveralUser({ usersList })),
  removeMemberFromCompany: (companyMember: ICompanyMember) =>
    appDispatch(removeMemberFromCompany({ companyMember })),
  addGuideToList: (key: Array<string>) => appDispatch(addGuideToList(key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyMembers);
